import PortfolioConfigData from 'config/portfolio-config.json';
import ComposizioneDelPortfolioImported from './components/ComposizioneDelPortfolioImported';
import React from 'react';
import commonStyles from 'pages/assistant/components/artifacts/components/fineco-portfolio/finecoPortfolio.common.module.scss';
import styles from './components/ComposizioneDelPortafoglio.module.scss';
import { FinecoDownloadButton } from 'pages/assistant/components/FinecoDownloadButton';
import { useTranslation } from 'react-i18next';
import { formatItalianNumber } from 'utils/formatUtils';

interface Order {
  isin: string;
  name?: string;
  productType?: string;
  // currency: string;
  market?: string; // the held listing's market: ISIN tooltip, not a column (ticket 150)
  operationType: string;
  amount: number;
  quantity: number;
  disinvestimento?: string;
  investimento?: string;
}

type SectionChildrenProps = {
  reportData: any;
  reportData2?: any;
  portfolioData?: any;
  requestGenerateExcel?: () => void;
  generatingExcel?: boolean;
};
const ComposizioneDelPortafoglioImportedContainer: React.FC<SectionChildrenProps> = ({
  reportData,
  reportData2,
  portfolioData,
  requestGenerateExcel,
  generatingExcel = false,
}) => {
  const { t } = useTranslation();
  if (!reportData) {
    return <></>;
  }

  // Extract and separate orders by operation type
  const orders: Order[] = portfolioData?.orders || [];
  const salesOrders = orders.filter((order) => order.operationType === 'V');
  const purchaseOrders = orders.filter((order) => order.operationType === 'A');

  // Calculate total countervalue from countervalue data if available, otherwise from orders
  let totalCountervalue = reportData?.countervalue?.total;

  // If countervalue.total is not available, calculate from orders
  // For imported portfolios, the total countervalue is the sum of purchase orders (current holdings)
  if (totalCountervalue === null || totalCountervalue === undefined) {
    const totalPurchases = purchaseOrders.reduce((sum, order) => sum + (order.amount || 0), 0);

    // Portfolio value = purchases (what you're buying/holding)
    totalCountervalue = totalPurchases;
  }

  const formattedTotal =
    totalCountervalue !== null && totalCountervalue !== undefined && totalCountervalue > 0
      ? formatItalianNumber(totalCountervalue)
      : undefined;

  // Column widths for orders table (matching portfolio table structure exactly)
  const ordersColumnWidths = [
    130, // ISIN
    130, // Tipo prodotto — widened so "CERTIFICATI" stays on one line
    670, // Nome
    110, // Quantità
    // 70, // Valuta
    110, // Controvalore (matching portfolio tables)
    120, // Disinvestimento / Investimento
  ];

  const headerStyle = {
    fontSize: '11px',
    fontWeight: 700,
    lineHeight: '12px',
  };

  const cellStyle = {
    fontSize: '14px',
  };

  const renderOrdersTable = (
    ordersList: Order[],
    extraColumnHeader: string,
    extraColumnField: 'disinvestimento' | 'investimento',
  ) => (
    <table className={styles.table}>
      <thead>
        <tr>
          <th
            style={{
              ...headerStyle,
              width: `${ordersColumnWidths[0]}px`,
              minWidth: `${ordersColumnWidths[0]}px`,
              maxWidth: `${ordersColumnWidths[0]}px`,
              textAlign: 'center',
            }}
          >
            ISIN
          </th>
          <th
            style={{
              ...headerStyle,
              width: `${ordersColumnWidths[1]}px`,
              minWidth: `${ordersColumnWidths[1]}px`,
              maxWidth: `${ordersColumnWidths[1]}px`,
              textAlign: 'center',
            }}
          >
            Tipo prodotto
          </th>
          <th
            style={{
              ...headerStyle,
              width: `${ordersColumnWidths[2]}px`,
              minWidth: `${ordersColumnWidths[2]}px`,
              maxWidth: `${ordersColumnWidths[2]}px`,
              textAlign: 'center',
            }}
          >
            Nome
          </th>
          <th
            style={{
              ...headerStyle,
              width: `${ordersColumnWidths[3]}px`,
              minWidth: `${ordersColumnWidths[3]}px`,
              maxWidth: `${ordersColumnWidths[3]}px`,
              textAlign: 'center',
            }}
          >
            Quantità
          </th>
          {/* <th
            style={{
              ...headerStyle,
              width: `${ordersColumnWidths[4]}px`,
              minWidth: `${ordersColumnWidths[4]}px`,
              maxWidth: `${ordersColumnWidths[4]}px`,
              textAlign: 'center',
            }}
          >
            Valuta
          </th> */}
          <th
            style={{
              ...headerStyle,
              width: `${ordersColumnWidths[4]}px`,
              minWidth: `${ordersColumnWidths[4]}px`,
              maxWidth: `${ordersColumnWidths[4]}px`,
              textAlign: 'center',
            }}
          >
            Controvalore
          </th>
          <th
            style={{
              ...headerStyle,
              width: `${ordersColumnWidths[5]}px`,
              minWidth: `${ordersColumnWidths[5]}px`,
              maxWidth: `${ordersColumnWidths[5]}px`,
              textAlign: 'center',
            }}
          >
            {extraColumnHeader}
          </th>
        </tr>
      </thead>
      <tbody>
        {ordersList.map((order, index) => (
          <tr key={index}>
            <td
              style={{
                ...cellStyle,
                width: `${ordersColumnWidths[0]}px`,
                minWidth: `${ordersColumnWidths[0]}px`,
                maxWidth: `${ordersColumnWidths[0]}px`,
                textAlign: 'center',
              }}
              title={order.market ? `Mercato: ${order.market}` : undefined}
            >
              {order.isin}
            </td>
            <td
              style={{
                ...cellStyle,
                width: `${ordersColumnWidths[1]}px`,
                minWidth: `${ordersColumnWidths[1]}px`,
                maxWidth: `${ordersColumnWidths[1]}px`,
                textAlign: 'center',
              }}
            >
              {order.productType || '-'}
            </td>
            <td
              style={{
                ...cellStyle,
                width: `${ordersColumnWidths[2]}px`,
                minWidth: `${ordersColumnWidths[2]}px`,
                maxWidth: `${ordersColumnWidths[2]}px`,
                textAlign: 'center',
              }}
            >
              {order.name || '-'}
            </td>
            <td
              style={{
                ...cellStyle,
                width: `${ordersColumnWidths[3]}px`,
                minWidth: `${ordersColumnWidths[3]}px`,
                maxWidth: `${ordersColumnWidths[3]}px`,
                textAlign: 'center',
              }}
            >
              {formatItalianNumber(order.quantity)}
            </td>
            {/* <td
              style={{
                ...cellStyle,
                width: `${ordersColumnWidths[4]}px`,
                minWidth: `${ordersColumnWidths[4]}px`,
                maxWidth: `${ordersColumnWidths[4]}px`,
                textAlign: 'center',
              }}
            >
              {order.currency}
            </td> */}
            <td
              style={{
                ...cellStyle,
                width: `${ordersColumnWidths[4]}px`,
                minWidth: `${ordersColumnWidths[4]}px`,
                maxWidth: `${ordersColumnWidths[4]}px`,
                textAlign: 'center',
              }}
            >
              {formatItalianNumber(order.amount)}
            </td>
            <td
              style={{
                ...cellStyle,
                width: `${ordersColumnWidths[5]}px`,
                minWidth: `${ordersColumnWidths[5]}px`,
                maxWidth: `${ordersColumnWidths[5]}px`,
                textAlign: 'center',
              }}
            >
              {order[extraColumnField] || '-'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      <div className={commonStyles.sectionContainer} style={{ marginTop: reportData2 ? 28 : 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className={commonStyles.titleChart}>
            {PortfolioConfigData.portfolio.data.strumenti_fondi.label} {reportData2 && '1'}
          </h3>
          {formattedTotal && !reportData2 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className={commonStyles.titleChart} style={{ fontWeight: 400 }}>
                Controvalore portafoglio
              </span>
              <span className={commonStyles.titleChart} style={{ fontWeight: 700 }}>
                €{formattedTotal}
              </span>
            </div>
          )}
        </div>
        <ComposizioneDelPortfolioImported
          strumenti_fondi={reportData.portafoglio_imported.strumenti_fondi}
          strumenti_obbligazionari={reportData.portafoglio_imported.strumenti_obbligazionari}
          strumenti_azioni={reportData.portafoglio_imported.strumenti_azioni || []}
          strumenti_certificati={reportData.portafoglio_imported.strumenti_certificati || []}
          strumenti_polizze={reportData.portafoglio_imported.strumenti_polizze || []}
          countervalue={reportData.countervalue}
        />
        {requestGenerateExcel && !reportData2 && (
          <div style={{ marginTop: 16 }}>
            <FinecoDownloadButton
              click={requestGenerateExcel}
              copy={!generatingExcel ? t('actions.generateExcel') : t('actions.submitting')}
            />
          </div>
        )}

        {/* Orders Tables */}
        {orders && orders.length > 0 && !reportData2 && (
          <div className={styles.complexTableContainer} style={{ marginTop: 24 }}>
            <h3 className={commonStyles.titleChart}>Lista operazioni</h3>

            {/* Sales Section */}
            {salesOrders.length > 0 && (
              <>
                <div className={styles.tableTitleContainer}>
                  <h4 className={styles.tableTitle}>Vendite</h4>
                  <div className={styles.tableSubtotal}>
                    <span className={styles.tableSubtotalLabel}>Totale</span>
                    <span className={styles.tableSubtotalValue}>
                      €{formatItalianNumber(salesOrders.reduce((acc, order) => acc + order.amount, 0))}
                    </span>
                  </div>
                </div>
                <div className={styles.tableContainerInner}>{renderOrdersTable(salesOrders, 'Disinvestimento', 'disinvestimento')}</div>
              </>
            )}

            {/* Purchases Section */}
            {purchaseOrders.length > 0 && (
              <>
                <div className={styles.tableTitleContainer}>
                  <h4 className={styles.tableTitle}>Acquisti</h4>
                  <div className={styles.tableSubtotal}>
                    <span className={styles.tableSubtotalLabel}>Totale</span>
                    <span className={styles.tableSubtotalValue}>
                      €{formatItalianNumber(purchaseOrders.reduce((acc, order) => acc + order.amount, 0))}
                    </span>
                  </div>
                </div>
                <div className={styles.tableContainerInner}>{renderOrdersTable(purchaseOrders, 'Investimento', 'investimento')}</div>
              </>
            )}
          </div>
        )}
      </div>

      {reportData2 && (
        <>
          <div className="force-page-break"></div>
          <div className={commonStyles.sectionContainerComposizioneDelPortafoglio}>
            <h3 className={commonStyles.titleChart} style={{ marginTop: 40 }}>
              {PortfolioConfigData.portfolio.data.strumenti_fondi.label}
              {' 2'}
            </h3>
            <ComposizioneDelPortfolioImported
              strumenti_fondi={reportData2.portafoglio.strumenti_fondi}
              strumenti_obbligazionari={reportData2.portafoglio.strumenti_obbligazionari}
              strumenti_azioni={reportData2.portafoglio.strumenti_azioni || []}
              strumenti_certificati={reportData2.portafoglio.strumenti_certificati || []}
              strumenti_polizze={reportData2.portafoglio.strumenti_polizze || []}
              countervalue={reportData2.countervalue}
            />
            <div style={{ marginBottom: 20 }}></div>
          </div>
        </>
      )}
    </>
  );
};
export default React.memo(
  ComposizioneDelPortafoglioImportedContainer,
  (prevProps: SectionChildrenProps, nextProps: SectionChildrenProps) => {
    // Custom comparison function
    return prevProps.reportData === nextProps.reportData;
  },
);
